import polygonClipping from 'polygon-clipping';

import {
  GENERATION_IMPACT_BOUNDARY_SCHEMA_VERSION,
  type GenerationImpactBoundaryPoint,
  type GenerationImpactParcelBoundarySnapshot,
} from '../../types/generationImpact';
import type { SiteEntity } from '../../types/site';
import type { SiteBoundaryParcel } from '../../types/siteBoundary';
import { makeParcel, polygonArea } from '../siteBoundaryGeometry';
import { applySiteStudyArea } from '../siteStudyArea';

export type GenerationImpactBoundarySite = Pick<SiteEntity, 'siteBoundaries' | 'siteBoundary' | 'siteLayers' | 'studyArea'>;

export const MAX_AUTHORITY_BOUNDARY_VERTICES_PER_RING = 2_000;

const SHA256_INITIAL = new Uint32Array([
  0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
  0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
]);

const SHA256_ROUND = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5,
  0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
  0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc,
  0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7,
  0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3,
  0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5,
  0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
  0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

function rotateRight(value: number, bits: number): number {
  return (value >>> bits) | (value << (32 - bits));
}

/** Browser-safe SHA-256 shared by persisted Generation & Impact integrity tuples. */
export function sha256HexForGenerationImpactValue(value: string): string {
  const bytes = new TextEncoder().encode(value);
  const paddedLength = Math.ceil((bytes.length + 9) / 64) * 64;
  const padded = new Uint8Array(paddedLength);
  padded.set(bytes);
  padded[bytes.length] = 0x80;
  const bitLength = bytes.length * 8;
  const view = new DataView(padded.buffer);
  view.setUint32(paddedLength - 8, Math.floor(bitLength / 0x1_0000_0000));
  view.setUint32(paddedLength - 4, bitLength >>> 0);

  const state = new Uint32Array(SHA256_INITIAL);
  const words = new Uint32Array(64);
  for (let offset = 0; offset < paddedLength; offset += 64) {
    for (let index = 0; index < 16; index += 1) {
      words[index] = view.getUint32(offset + index * 4);
    }
    for (let index = 16; index < 64; index += 1) {
      const left = words[index - 15]!;
      const right = words[index - 2]!;
      const sigma0 = rotateRight(left, 7) ^ rotateRight(left, 18) ^ (left >>> 3);
      const sigma1 = rotateRight(right, 17) ^ rotateRight(right, 19) ^ (right >>> 10);
      words[index] = (words[index - 16]! + sigma0 + words[index - 7]! + sigma1) >>> 0;
    }

    let a = state[0]!;
    let b = state[1]!;
    let c = state[2]!;
    let d = state[3]!;
    let e = state[4]!;
    let f = state[5]!;
    let g = state[6]!;
    let h = state[7]!;
    for (let index = 0; index < 64; index += 1) {
      const sum1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25);
      const choose = (e & f) ^ (~e & g);
      const temp1 = (h + sum1 + choose + SHA256_ROUND[index]! + words[index]!) >>> 0;
      const sum0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);
      const majority = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (sum0 + majority) >>> 0;
      h = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }
    state[0] = (state[0]! + a) >>> 0;
    state[1] = (state[1]! + b) >>> 0;
    state[2] = (state[2]! + c) >>> 0;
    state[3] = (state[3]! + d) >>> 0;
    state[4] = (state[4]! + e) >>> 0;
    state[5] = (state[5]! + f) >>> 0;
    state[6] = (state[6]! + g) >>> 0;
    state[7] = (state[7]! + h) >>> 0;
  }
  return Array.from(state, (word) => word.toString(16).padStart(8, '0')).join('');
}

/** Backwards-compatible boundary-specific name retained for existing callers/tests. */
export const sha256HexForGenerationImpactBoundary = sha256HexForGenerationImpactValue;

function samePoint(left: GenerationImpactBoundaryPoint, right: GenerationImpactBoundaryPoint): boolean {
  return left.lat === right.lat && left.lng === right.lng;
}

function cleanRing(points: readonly GenerationImpactBoundaryPoint[]): GenerationImpactBoundaryPoint[] {
  const cleaned: GenerationImpactBoundaryPoint[] = [];
  for (const raw of points) {
    if (!raw || !Number.isFinite(raw.lat) || !Number.isFinite(raw.lng)
      || raw.lat < -85 || raw.lat > 85 || raw.lng < -180 || raw.lng > 180) {
      throw new Error('Parcel boundary contains an invalid latitude/longitude coordinate');
    }
    const point = {
      lat: Object.is(raw.lat, -0) ? 0 : raw.lat,
      lng: Object.is(raw.lng, -0) ? 0 : raw.lng,
    };
    if (cleaned.length === 0 || !samePoint(cleaned[cleaned.length - 1]!, point)) cleaned.push(point);
  }
  if (cleaned.length > 1 && samePoint(cleaned[0]!, cleaned[cleaned.length - 1]!)) cleaned.pop();
  if (cleaned.length < 3) throw new Error('Parcel boundary must contain at least three distinct vertices');
  return cleaned;
}

function planarSignedArea(points: readonly GenerationImpactBoundaryPoint[]): number {
  let area = 0;
  for (let index = 0; index < points.length; index += 1) {
    const left = points[index]!;
    const right = points[(index + 1) % points.length]!;
    area += left.lng * right.lat - right.lng * left.lat;
  }
  return area / 2;
}

function orientation(
  first: GenerationImpactBoundaryPoint,
  second: GenerationImpactBoundaryPoint,
  third: GenerationImpactBoundaryPoint,
): number {
  const cross = (second.lng - first.lng) * (third.lat - first.lat)
    - (second.lat - first.lat) * (third.lng - first.lng);
  if (Math.abs(cross) <= 1e-14) return 0;
  return cross > 0 ? 1 : -1;
}

function onSegment(
  first: GenerationImpactBoundaryPoint,
  point: GenerationImpactBoundaryPoint,
  second: GenerationImpactBoundaryPoint,
): boolean {
  const epsilon = 1e-14;
  return point.lng >= Math.min(first.lng, second.lng) - epsilon
    && point.lng <= Math.max(first.lng, second.lng) + epsilon
    && point.lat >= Math.min(first.lat, second.lat) - epsilon
    && point.lat <= Math.max(first.lat, second.lat) + epsilon;
}

function segmentsIntersect(
  a: GenerationImpactBoundaryPoint,
  b: GenerationImpactBoundaryPoint,
  c: GenerationImpactBoundaryPoint,
  d: GenerationImpactBoundaryPoint,
): boolean {
  const abC = orientation(a, b, c);
  const abD = orientation(a, b, d);
  const cdA = orientation(c, d, a);
  const cdB = orientation(c, d, b);
  if (abC !== abD && cdA !== cdB) return true;
  return (abC === 0 && onSegment(a, c, b))
    || (abD === 0 && onSegment(a, d, b))
    || (cdA === 0 && onSegment(c, a, d))
    || (cdB === 0 && onSegment(c, b, d));
}

/**
 * Which edge pairs of a closed ring cross each other.
 *
 * This is the DIAGNOSTIC form of the check `assertSimpleRing` enforces, and that
 * function is implemented in terms of this one so the two can never disagree
 * about what "self-intersecting" means. Callers that want to explain or repair a
 * rejected polygon — rather than merely reject it — use this.
 *
 * `points` is the OPEN ring: no repeated closing vertex. Indices returned are
 * edge indices, where edge i runs from points[i] to points[(i + 1) % length].
 */
export function findRingSelfIntersections(
  points: readonly GenerationImpactBoundaryPoint[],
): Array<[number, number]> {
  const hits: Array<[number, number]> = [];
  for (let first = 0; first < points.length; first += 1) {
    const firstNext = (first + 1) % points.length;
    for (let second = first + 1; second < points.length; second += 1) {
      const secondNext = (second + 1) % points.length;
      if (first === second || firstNext === second || secondNext === first) continue;
      if (segmentsIntersect(points[first]!, points[firstNext]!, points[second]!, points[secondNext]!)) {
        hits.push([first, second]);
      }
    }
  }
  return hits;
}

function assertSimpleRing(points: readonly GenerationImpactBoundaryPoint[]): void {
  if (points.length > MAX_AUTHORITY_BOUNDARY_VERTICES_PER_RING) {
    throw new Error(
      `Parcel boundary has ${points.length} vertices; simplify each parcel to ${MAX_AUTHORITY_BOUNDARY_VERTICES_PER_RING} vertices or fewer before rebuilding the SED`,
    );
  }
  if (findRingSelfIntersections(points).length > 0) {
    throw new Error('Parcel boundary self-intersects and cannot be used for a spatial-fit authority');
  }
  if (Math.abs(planarSignedArea(points)) <= 1e-14 || polygonArea(points) <= 0) {
    throw new Error('Parcel boundary is degenerate and has no usable area');
  }
}

function comparePoint(left: GenerationImpactBoundaryPoint, right: GenerationImpactBoundaryPoint): number {
  if (left.lat !== right.lat) return left.lat < right.lat ? -1 : 1;
  if (left.lng !== right.lng) return left.lng < right.lng ? -1 : 1;
  return 0;
}

function rotateToLowestPoint(points: readonly GenerationImpactBoundaryPoint[]): GenerationImpactBoundaryPoint[] {
  let lowest = 0;
  for (let index = 1; index < points.length; index += 1) {
    if (comparePoint(points[index]!, points[lowest]!) < 0) lowest = index;
  }
  return [...points.slice(lowest), ...points.slice(0, lowest)].map((point) => ({ ...point }));
}

function canonicalRing(
  raw: readonly GenerationImpactBoundaryPoint[],
  kind: 'outer' | 'hole',
): GenerationImpactBoundaryPoint[] {
  let points = cleanRing(raw);
  assertSimpleRing(points);
  const shouldBeCounterClockwise = kind === 'outer';
  if ((planarSignedArea(points) > 0) !== shouldBeCounterClockwise) points = [...points].reverse();
  return rotateToLowestPoint(points);
}

function closeLngLatRing(points: readonly GenerationImpactBoundaryPoint[]): [number, number][] {
  const ring = points.map((point): [number, number] => [point.lng, point.lat]);
  ring.push([points[0]!.lng, points[0]!.lat]);
  return ring;
}

function ringKey(points: readonly GenerationImpactBoundaryPoint[]): string {
  return JSON.stringify(points.map(({ lat, lng }) => [lat, lng]));
}

function polygonKey(polygon: readonly (readonly GenerationImpactBoundaryPoint[])[]): string {
  return JSON.stringify(polygon.map((ring) => ring.map(({ lat, lng }) => [lat, lng])));
}

function canonicalPolygon(
  raw: readonly (readonly GenerationImpactBoundaryPoint[])[],
): GenerationImpactBoundaryPoint[][] {
  if (raw.length === 0) throw new Error('Parcel boundary contains an empty polygon');
  const outer = canonicalRing(raw[0]!, 'outer');
  const holes = raw.slice(1).map((ring) => canonicalRing(ring, 'hole'));
  holes.sort((left, right) => ringKey(left).localeCompare(ringKey(right)));
  return [outer, ...holes];
}

function unionCanonicalPolygons(
  rawPolygons: readonly (readonly (readonly GenerationImpactBoundaryPoint[])[])[],
): GenerationImpactBoundaryPoint[][][] {
  if (rawPolygons.length === 0) throw new Error('A parcel boundary polygon is required before rebuilding the SED');
  const inputs = rawPolygons.map(canonicalPolygon)
    .sort((left, right) => polygonKey(left).localeCompare(polygonKey(right)));
  const polygons = inputs.map((polygon) => (
    polygon.map(closeLngLatRing) as polygonClipping.Polygon
  ));
  let union: polygonClipping.MultiPolygon;
  try {
    union = polygonClipping.union(polygons[0]!, ...polygons.slice(1)) as polygonClipping.MultiPolygon;
  } catch (error) {
    throw new Error(`Parcel boundary union failed: ${(error as Error)?.message ?? 'invalid polygon geometry'}`);
  }
  const canonical = union.map((polygon) => {
    if (polygon.length === 0) throw new Error('Parcel boundary union produced an empty polygon');
    return canonicalPolygon(polygon.map((ring) => ring.map(([lng, lat]) => ({ lat, lng }))));
  });
  canonical.sort((left, right) => polygonKey(left).localeCompare(polygonKey(right)));
  if (canonical.length === 0) throw new Error('Parcel boundary union produced no usable geometry');
  return canonical;
}

function boundaryHashMaterial(
  source: GenerationImpactParcelBoundarySnapshot['source'],
  polygons: readonly (readonly (readonly GenerationImpactBoundaryPoint[])[])[],
  areaAcres: number,
): string {
  return JSON.stringify({
    schemaVersion: GENERATION_IMPACT_BOUNDARY_SCHEMA_VERSION,
    source,
    polygons,
    areaAcres,
  });
}

function createFromPolygons(
  source: GenerationImpactParcelBoundarySnapshot['source'],
  rawPolygons: readonly (readonly (readonly GenerationImpactBoundaryPoint[])[])[],
): GenerationImpactParcelBoundarySnapshot {
  const polygons = unionCanonicalPolygons(rawPolygons);
  const areaAcres = polygons.reduce((total, polygon) => {
    const [outer, ...holes] = polygon;
    if (!outer) return total;
    return total + polygonArea(outer) - holes.reduce((sum, hole) => sum + polygonArea(hole), 0);
  }, 0);
  if (!Number.isFinite(areaAcres) || areaAcres <= 0) {
    throw new Error('Parcel boundary union has no finite positive area');
  }
  return {
    schemaVersion: GENERATION_IMPACT_BOUNDARY_SCHEMA_VERSION,
    source,
    polygons,
    areaAcres,
    boundaryHash: sha256HexForGenerationImpactBoundary(boundaryHashMaterial(source, polygons, areaAcres)),
  };
}

/** Build the immutable canonical boundary consumed by Generation & Impact Test Fit. */
export function createGenerationImpactParcelBoundarySnapshot(
  site: GenerationImpactBoundarySite,
): GenerationImpactParcelBoundarySnapshot {
  return createRawParcelBoundarySnapshot(applySiteStudyArea(site));
}

/** Low-level geometry only. The study resolver uses this to avoid resolving itself. */
export function createRawParcelBoundarySnapshot(
  site: Pick<SiteEntity, 'siteBoundaries' | 'siteBoundary'>,
): GenerationImpactParcelBoundarySnapshot {
  if (Array.isArray(site.siteBoundaries) && site.siteBoundaries.length > 0) {
    return createFromPolygons(
      'site-boundaries',
      site.siteBoundaries.map((parcel) => [parcel.polygon, ...(parcel.holes ?? [])]),
    );
  }
  if (Array.isArray(site.siteBoundary) && site.siteBoundary.length > 0) {
    return createFromPolygons('site-boundary', [[site.siteBoundary]]);
  }
  throw new Error('A valid parcel boundary must be drawn before rebuilding a Generation & Impact SED');
}

/** Return a fail-loud structural/integrity reason, or null when valid. */
export function validateGenerationImpactParcelBoundarySnapshot(
  snapshot: GenerationImpactParcelBoundarySnapshot | null | undefined,
): string | null {
  if (!snapshot || typeof snapshot !== 'object') return 'Frozen parcel boundary snapshot is missing';
  if (snapshot.schemaVersion !== GENERATION_IMPACT_BOUNDARY_SCHEMA_VERSION) {
    return 'Frozen parcel boundary uses an unsupported schema version';
  }
  if (snapshot.source !== 'site-boundaries' && snapshot.source !== 'site-boundary') {
    return 'Frozen parcel boundary source is invalid';
  }
  if (!Array.isArray(snapshot.polygons) || snapshot.polygons.length === 0) {
    return 'Frozen parcel boundary geometry is empty';
  }
  if (!Number.isFinite(snapshot.areaAcres) || snapshot.areaAcres <= 0) {
    return 'Frozen parcel boundary area must be finite and positive';
  }
  if (!/^[a-f0-9]{64}$/.test(snapshot.boundaryHash)) {
    return 'Frozen parcel boundary hash is invalid';
  }
  try {
    const rebuilt = createFromPolygons(snapshot.source, snapshot.polygons);
    if (rebuilt.boundaryHash !== snapshot.boundaryHash
      || rebuilt.areaAcres !== snapshot.areaAcres
      || JSON.stringify(rebuilt.polygons) !== JSON.stringify(snapshot.polygons)) {
      return 'Frozen parcel boundary geometry, area, or integrity hash does not match its canonical form';
    }
  } catch (error) {
    return `Frozen parcel boundary is invalid: ${(error as Error)?.message ?? 'invalid geometry'}`;
  }
  return null;
}

export function sameGenerationImpactParcelBoundary(
  left: GenerationImpactParcelBoundarySnapshot | null | undefined,
  right: GenerationImpactParcelBoundarySnapshot | null | undefined,
): boolean {
  return Boolean(
    left
      && right
      && left.schemaVersion === right.schemaVersion
      && left.source === right.source
      && left.boundaryHash === right.boundaryHash
      && left.areaAcres === right.areaAcres,
  );
}

function pointInRing(
  point: GenerationImpactBoundaryPoint,
  ring: readonly GenerationImpactBoundaryPoint[],
): boolean {
  let inside = false;
  for (
    let leftIndex = 0, rightIndex = ring.length - 1;
    leftIndex < ring.length;
    rightIndex = leftIndex++
  ) {
    const left = ring[leftIndex]!;
    const right = ring[rightIndex]!;
    const crosses = (left.lat > point.lat) !== (right.lat > point.lat)
      && point.lng < (
        (right.lng - left.lng) * (point.lat - left.lat)
        / ((right.lat - left.lat) || Number.EPSILON)
        + left.lng
      );
    if (crosses) inside = !inside;
  }
  return inside;
}

/**
 * Materialize canonical, detached parcels directly from a frozen authority
 * boundary for the existing single-ring layout packer.
 *
 * MultiPolygon components remain separate parcels and the preferred anchor
 * selects the physical component used by the scenario layout. Interior holes
 * cannot be represented by `SiteBoundaryParcel`; rather than silently filling
 * them and producing a non-physical fit, this adapter fails closed. The
 * authoritative Test Fit projection continues to support the full
 * MultiPolygon-with-holes contract directly.
 */
export function materializeGenerationImpactAuthorityParcels(
  snapshot: GenerationImpactParcelBoundarySnapshot,
  options: {
    targetParcelId?: string;
    preferredAnchor?: GenerationImpactBoundaryPoint;
  } = {},
): SiteBoundaryParcel[] {
  const invalidReason = validateGenerationImpactParcelBoundarySnapshot(snapshot);
  if (invalidReason) throw new Error(invalidReason);
  if (snapshot.polygons.some(polygon => polygon.length > 1)) {
    throw new Error(
      'Frozen parcel boundary contains interior holes that the generation layout exhibit packer cannot represent without geometry loss',
    );
  }

  const selectedIndex = (() => {
    const anchor = options.preferredAnchor;
    if (anchor) {
      const containing = snapshot.polygons.findIndex(polygon => (
        polygon[0] != null && pointInRing(anchor, polygon[0])
      ));
      if (containing >= 0) return containing;
    }
    let largestIndex = 0;
    let largestArea = Number.NEGATIVE_INFINITY;
    snapshot.polygons.forEach((polygon, index) => {
      const area = polygon[0] ? polygonArea(polygon[0]) : 0;
      if (area > largestArea) {
        largestArea = area;
        largestIndex = index;
      }
    });
    return largestIndex;
  })();

  return snapshot.polygons.map((polygon, index) => {
    const outer = polygon[0]!;
    const targetParcelId = options.targetParcelId?.trim();
    const id = index === selectedIndex && targetParcelId
      ? targetParcelId
      : `generation-impact-authority-parcel-${index + 1}`;
    return makeParcel(
      outer.map(point => ({ ...point })),
      {
        id,
        label: snapshot.polygons.length === 1
          ? 'Frozen authority boundary'
          : `Frozen authority boundary ${index + 1}`,
        intendedUse: index === selectedIndex ? 'datacenter' : 'other',
      },
    );
  });
}
